// Calendar (design "Calendar"): Day / Week / Month views of the organizer's
// schedule-lite meetings (F-PLAT-7). The time gates nothing — every event is a
// link into its meeting; clicking one opens an info modal with a Start action.

import { CSSProperties, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { api, Me, MeetingCreated, MeetingSummary } from '../api';
import { MeetingInfoModal } from '../components/MeetingInfoModal';
import { ScheduleDrawer } from '../components/ScheduleDrawer';
import { TopBar } from '../components/TopBar';
import { IconButton } from '../ui/IconButton';
import { IconChevronLeft, IconChevronRight, IconPlus } from '../ui/icons';

type View = 'day' | 'week' | 'month';

const VIEWS: View[] = ['day', 'week', 'month'];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const HOUR_H = 52; // px per hour in the day/week time grid
const EVENT_MIN = 60; // assumed visual length of a schedule-lite event (no duration field)

// Stable style objects (react-perf: never create props inline).
const HOUR_STYLE: CSSProperties = { height: HOUR_H };
const GRID_STYLE: CSSProperties = { height: 24 * HOUR_H };
const teventStyle = (startMin: number, lane: number, lanes: number): CSSProperties => {
  const w = 100 / lanes;
  return {
    top: (startMin / 60) * HOUR_H,
    height: (EVENT_MIN / 60) * HOUR_H - 3,
    left: `calc(${lane * w}% + 2px)`,
    width: `calc(${w}% - 4px)`,
  };
};
const nowStyle = (nowMin: number): CSSProperties => ({ top: (nowMin / 60) * HOUR_H });

const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number): Date => {
  const x = startOfDay(d);
  x.setDate(x.getDate() + n);
  return x;
};
const sameDay = (a: Date, b: Date): boolean => a.toDateString() === b.toDateString();
const mondayOf = (d: Date): Date => addDays(d, -((d.getDay() + 6) % 7));
const shortTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
const hourLabel = (h: number): string =>
  new Date(0, 0, 0, h).toLocaleTimeString(undefined, { hour: 'numeric' });
const eventClass = (m: MeetingSummary, base: string): string =>
  m.e2eeModel === 'b' ? `${base} ${base}-zk` : base;

const eventsOn = (meetings: MeetingSummary[], day: Date): MeetingSummary[] =>
  meetings
    .filter((m) => m.scheduledAt && sameDay(new Date(m.scheduledAt), day))
    .sort((a, b) => new Date(a.scheduledAt!).getTime() - new Date(b.scheduledAt!).getTime());

// ── Overlap lanes: side-by-side layout for events that share a time window ───
interface Laid {
  ev: MeetingSummary;
  startMin: number;
  lane: number;
  lanes: number;
}
function layoutDay(events: MeetingSummary[]): Laid[] {
  const items = events
    .map((ev) => {
      const d = new Date(ev.scheduledAt!);
      const startMin = d.getHours() * 60 + d.getMinutes();
      return { ev, startMin, endMin: startMin + EVENT_MIN, lane: 0 };
    })
    .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
  const out: Laid[] = [];
  let cluster: typeof items = [];
  let clusterEnd = -1;
  const flush = (): void => {
    const laneEnds: number[] = [];
    for (const it of cluster) {
      let lane = laneEnds.findIndex((e) => e <= it.startMin);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(it.endMin);
      } else {
        laneEnds[lane] = it.endMin;
      }
      it.lane = lane;
    }
    for (const it of cluster)
      out.push({ ev: it.ev, startMin: it.startMin, lane: it.lane, lanes: laneEnds.length });
    cluster = [];
  };
  for (const it of items) {
    if (cluster.length && it.startMin >= clusterEnd) flush();
    cluster.push(it);
    clusterEnd = Math.max(clusterEnd, it.endMin);
  }
  flush();
  return out;
}

export function CalendarPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState<Me | null>(null);
  const [meetings, setMeetings] = useState<MeetingSummary[]>([]);
  const [view, setView] = useState<View>('month');
  const [anchor, setAnchor] = useState<Date>(() => startOfDay(new Date()));
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [selected, setSelected] = useState<MeetingSummary | null>(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    void api
      .me()
      .then(setMe)
      .catch(() => void navigate('/'));
  }, [navigate]);

  useEffect(() => {
    void api
      .listMeetings()
      .then(setMeetings)
      .catch(() => {});
  }, [refresh]);

  const today = startOfDay(new Date());

  const goPrev = useCallback(() => {
    setAnchor((a) =>
      view === 'month' ? new Date(a.getFullYear(), a.getMonth() - 1, 1) : addDays(a, view === 'week' ? -7 : -1),
    );
  }, [view]);
  const goNext = useCallback(() => {
    setAnchor((a) =>
      view === 'month' ? new Date(a.getFullYear(), a.getMonth() + 1, 1) : addDays(a, view === 'week' ? 7 : 1),
    );
  }, [view]);
  const goToday = useCallback(() => setAnchor(startOfDay(new Date())), []);
  const pickView = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    setView(e.currentTarget.dataset.view as View);
  }, []);
  const openDay = useCallback((d: Date) => {
    setAnchor(startOfDay(d));
    setView('day');
  }, []);
  const openEvent = useCallback((m: MeetingSummary) => setSelected(m), []);

  const openSchedule = useCallback(() => setScheduleOpen(true), []);
  const closeSchedule = useCallback(() => setScheduleOpen(false), []);
  const onScheduled = useCallback((_m: MeetingCreated) => setRefresh((n) => n + 1), []);
  const onSignedOut = useCallback(() => void navigate('/'), [navigate]);
  const closeEvent = useCallback(() => setSelected(null), []);
  const startMeeting = useCallback(
    (slug: string) => {
      setSelected(null);
      void navigate(`/m/${slug}`);
    },
    [navigate],
  );

  const title = useMemo(() => {
    if (view === 'month')
      return anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    if (view === 'day')
      return anchor.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
    const start = mondayOf(anchor);
    const end = addDays(start, 6);
    const sameMonth = start.getMonth() === end.getMonth();
    const startFmt = start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const endFmt = end.toLocaleDateString(
      undefined,
      sameMonth ? { day: 'numeric' } : { month: 'short', day: 'numeric' },
    );
    return `${startFmt} – ${endFmt}, ${end.getFullYear()}`;
  }, [view, anchor]);

  const gridDays = useMemo(() => {
    if (view === 'day') return [anchor];
    const start = mondayOf(anchor);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [view, anchor]);

  if (!me) return <div className="bz-state-page">Loading…</div>;

  return (
    <div className="bz-cal">
      <TopBar me={me} section="Calendar" onSignedOut={onSignedOut} />
      <div className="bz-cal-toolbar">
        <div className="bz-cal-tbleft">
          <button type="button" className="bz-cal-today" onClick={goToday}>
            Today
          </button>
          <div className="bz-cal-nav">
            <IconButton shape="flat" label="Previous" onClick={goPrev}>
              <IconChevronLeft size={16} />
            </IconButton>
            <IconButton shape="flat" label="Next" onClick={goNext}>
              <IconChevronRight size={16} />
            </IconButton>
          </div>
          <h2 className="bz-cal-title">{title}</h2>
        </div>
        <div className="bz-cal-tbright">
          <div className="bz-cal-viewswitch" role="group" aria-label="Calendar view">
            {VIEWS.map((v) => (
              <button
                key={v}
                type="button"
                data-view={v}
                className={view === v ? 'is-active' : ''}
                aria-pressed={view === v}
                onClick={pickView}
              >
                {v[0]!.toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="bz-cal-add"
            onClick={openSchedule}
            aria-label="Schedule a meeting"
            title="Schedule a meeting"
          >
            <IconPlus size={20} />
          </button>
        </div>
      </div>

      <div className="bz-cal-body">
        {view === 'month' ? (
          <MonthGrid anchor={anchor} today={today} meetings={meetings} onEvent={openEvent} onDay={openDay} />
        ) : (
          <TimeGrid days={gridDays} today={today} meetings={meetings} onEvent={openEvent} />
        )}
      </div>

      {scheduleOpen && <ScheduleDrawer onClose={closeSchedule} onCreated={onScheduled} />}
      {selected && <MeetingInfoModal meeting={selected} onClose={closeEvent} onStart={startMeeting} />}
    </div>
  );
}

// ── Month grid ───────────────────────────────────────────────────────────────
function MonthGrid({
  anchor,
  today,
  meetings,
  onEvent,
  onDay,
}: {
  anchor: Date;
  today: Date;
  meetings: MeetingSummary[];
  onEvent: (m: MeetingSummary) => void;
  onDay: (d: Date) => void;
}) {
  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const cells = useMemo(() => {
    const first = new Date(year, month, 1);
    const lead = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const out: (Date | null)[] = [];
    for (let i = 0; i < lead; i++) out.push(null);
    for (let d = 1; d <= daysInMonth; d++) out.push(new Date(year, month, d));
    while (out.length % 7 !== 0 || out.length < 35) out.push(null);
    return out;
  }, [year, month]);

  const onDayClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => onDay(new Date(e.currentTarget.dataset.date!)),
    [onDay],
  );
  const onEventClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const m = meetings.find((x) => x.slug === e.currentTarget.dataset.slug);
      if (m) onEvent(m);
    },
    [meetings, onEvent],
  );

  return (
    <div className="bz-cal-month">
      <div className="bz-cal-weekdays">
        {WEEKDAYS.map((wd) => (
          <span key={wd} className="bz-cal-weekday">
            {wd}
          </span>
        ))}
      </div>
      <div className="bz-cal-grid">
        {cells.map((date, i) => {
          if (!date) return <div key={i} className="bz-cal-cell bz-cal-cell-empty" />;
          const iso = date.toISOString();
          const isToday = sameDay(date, today);
          const dayEvents = eventsOn(meetings, date);
          const shown = dayEvents.slice(0, 3);
          const extra = dayEvents.length - shown.length;
          return (
            <div key={i} className={isToday ? 'bz-cal-cell bz-cal-cell-today' : 'bz-cal-cell'}>
              <button
                type="button"
                className="bz-cal-day"
                data-date={iso}
                onClick={onDayClick}
                aria-label={`Open ${date.toDateString()}`}
              >
                {date.getDate()}
              </button>
              {shown.map((ev) => (
                <button
                  key={ev.slug}
                  type="button"
                  className={eventClass(ev, 'bz-cal-event')}
                  data-slug={ev.slug}
                  onClick={onEventClick}
                >
                  <span className="bz-cal-event-dot" aria-hidden="true" />
                  {shortTime(ev.scheduledAt!)} {ev.title ?? ev.slug}
                </button>
              ))}
              {extra > 0 && (
                <button type="button" className="bz-cal-more" data-date={iso} onClick={onDayClick}>
                  +{extra} more
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Day / Week time grid ─────────────────────────────────────────────────────
function TimeGrid({
  days,
  today,
  meetings,
  onEvent,
}: {
  days: Date[];
  today: Date;
  meetings: MeetingSummary[];
  onEvent: (m: MeetingSummary) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [now, setNow] = useState(() => new Date());

  const onEventClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const m = meetings.find((x) => x.slug === e.currentTarget.dataset.slug);
      if (m) onEvent(m);
    },
    [meetings, onEvent],
  );

  // Tick the "now" line every minute (cheap — a handful of events).
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(t);
  }, []);

  // Open scrolled to business hours (or the earliest event of the range).
  const rangeKey = days[0]?.toDateString() ?? '';
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let firstHour = 8;
    for (const d of days)
      for (const m of eventsOn(meetings, d))
        firstHour = Math.min(firstHour, new Date(m.scheduledAt!).getHours());
    el.scrollTop = Math.max(0, (firstHour - 0.5) * HOUR_H);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-scroll only when the visible range changes
  }, [rangeKey, days.length]);

  const nowMin = now.getHours() * 60 + now.getMinutes();

  return (
    <div className="bz-cal-time">
      <div className="bz-cal-time-head">
        <div className="bz-cal-time-gutterhead" />
        {days.map((d) => (
          <div
            key={d.toISOString()}
            className={sameDay(d, today) ? 'bz-cal-dhead bz-cal-dhead-today' : 'bz-cal-dhead'}
          >
            <span className="bz-cal-dhead-wd">{d.toLocaleDateString(undefined, { weekday: 'short' })}</span>
            <span className="bz-cal-dhead-num">{d.getDate()}</span>
          </div>
        ))}
      </div>
      <div className="bz-cal-time-scroll" ref={scrollRef}>
        <div className="bz-cal-time-grid" style={GRID_STYLE}>
          <div className="bz-cal-gutter">
            {HOURS.map((h) => (
              <div key={h} className="bz-cal-hour" style={HOUR_STYLE}>
                {h > 0 && <span>{hourLabel(h)}</span>}
              </div>
            ))}
          </div>
          <div className="bz-cal-daycols">
            {days.map((d) => {
              const isToday = sameDay(d, today);
              const laid = layoutDay(eventsOn(meetings, d));
              return (
                <div key={d.toISOString()} className="bz-cal-daycol">
                  {HOURS.map((h) => (
                    <div key={h} className="bz-cal-tslot" style={HOUR_STYLE} />
                  ))}
                  {isToday && (
                    <div className="bz-cal-now" style={nowStyle(nowMin)}>
                      <span className="bz-cal-now-dot" />
                    </div>
                  )}
                  {laid.map(({ ev, startMin, lane, lanes }) => (
                    <button key={ev.slug} type="button" className={eventClass(ev, 'bz-cal-tevent')} data-slug={ev.slug} onClick={onEventClick} style={teventStyle(startMin, lane, lanes)}>
                      <span className="bz-cal-tevent-title">{ev.title ?? ev.slug}</span>
                      <span className="bz-cal-tevent-time">{shortTime(ev.scheduledAt!)}</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
